<script setup lang="ts">
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import { useCustomerSavings } from '@/composables/cpm/customerSavings'
import type { ICustomer } from '@/types/core/Customer'
import type {
  ICustomerSavingsCreationPayload,
  ICustomerSavingsRecord,
  ICustomerSavingsUpdatePayload,
} from '@/types/cpm'
import type { FormInstance, FormRules } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, reactive, ref, watch } from 'vue'

const visible = defineModel<boolean>()

const props = defineProps<{
  /** When present, the dialog is in edit mode (site / year / month are immutable). */
  record?: ICustomerSavingsRecord
  initialCustomer?: ICustomer
  initialSite?: ISiteIdentifier
}>()

const { createCustomerSavingsMutation, updateCustomerSavingsMutation } = useCustomerSavings()

const isEditMode = computed(() => !!props.record)

// ── Month / year options ──
// Savings are only recorded for past/current periods — never the future.
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const now = new Date()
const currentYear = now.getFullYear()
const currentMonth = now.getMonth() + 1

const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i)

// For the current year, cap the month at the current month; earlier years allow all 12.
const maxMonthForSelectedYear = computed(() => (form.year >= currentYear ? currentMonth : 12))
const months = computed(() =>
  MONTH_LABELS.slice(0, maxMonthForSelectedYear.value).map((label, index) => ({ label, value: index + 1 }))
)

const formRef = ref<FormInstance>()
const isSubmitting = ref(false)

const selectedCustomer = ref<ICustomer | undefined>()
const selectedSite = ref<ISiteIdentifier | undefined>()

interface SavingsForm {
  year: number
  month: number
  average_hourly_consumption_kwh: number | undefined
  cp_hour_load_kwh: number | undefined
  cp_schedule_rate_usd_per_kwh: number | undefined
  total_savings_usd: number | undefined
  cp_management_fee_usd: number | undefined
}

const form = reactive<SavingsForm>({
  year: currentYear,
  month: currentMonth,
  average_hourly_consumption_kwh: undefined,
  cp_hour_load_kwh: undefined,
  cp_schedule_rate_usd_per_kwh: undefined,
  total_savings_usd: undefined,
  cp_management_fee_usd: undefined,
})

// Keep the month in range when the year changes (e.g. switching to the current year
// must not leave a future month selected).
watch(
  () => form.year,
  () => {
    if (isEditMode.value) return
    if (form.month > maxMonthForSelectedYear.value) form.month = maxMonthForSelectedYear.value
  }
)

const rules = reactive<FormRules<SavingsForm>>({
  average_hourly_consumption_kwh: [
    { required: true, type: 'number', message: 'Please enter the average hourly load', trigger: 'blur' },
  ],
  cp_hour_load_kwh: [{ required: true, type: 'number', message: 'Please enter the CP hour load', trigger: 'blur' }],
  cp_schedule_rate_usd_per_kwh: [
    { required: true, type: 'number', message: 'Please enter the CP schedule rate', trigger: 'blur' },
  ],
  total_savings_usd: [{ required: true, type: 'number', message: 'Please enter the total savings', trigger: 'blur' }],
  cp_management_fee_usd: [
    { required: true, type: 'number', message: 'Please enter the CP management fee', trigger: 'blur' },
  ],
})

// For create mode, a site must be selected before submitting.
const canSubmit = computed(() => isEditMode.value || !!selectedSite.value?.site_id)

const editPeriodLabel = computed(() => {
  if (!props.record) return ''
  return DateTime.fromObject({ year: props.record.year, month: props.record.month }).toFormat('MMMM yyyy')
})

const resetForm = () => {
  selectedCustomer.value = undefined
  selectedSite.value = undefined
  form.year = currentYear
  form.month = new Date().getMonth() + 1
  form.average_hourly_consumption_kwh = undefined
  form.cp_hour_load_kwh = undefined
  form.cp_schedule_rate_usd_per_kwh = undefined
  form.total_savings_usd = undefined
  form.cp_management_fee_usd = undefined
  formRef.value?.clearValidate()
}

function parseDecimal(val: string) {
  const parsed = parseFloat(val)
  return Number.isNaN(parsed) ? undefined : parsed
}

const onDialogOpened = () => {
  resetForm()

  if (props.record) {
    // Edit mode: prefill the figures from the record (parse DRF Decimal strings).
    form.year = props.record.year
    form.month = props.record.month
    form.average_hourly_consumption_kwh = parseDecimal(props.record.average_hourly_consumption_kwh)
    form.cp_hour_load_kwh = parseDecimal(props.record.cp_hour_load_kwh)
    form.cp_schedule_rate_usd_per_kwh = parseDecimal(props.record.cp_schedule_rate_usd_per_kwh)
    form.total_savings_usd = parseDecimal(props.record.total_savings_usd)
    form.cp_management_fee_usd = parseDecimal(props.record.cp_management_fee_usd)
    return
  }

  // Create mode: seed customer / site from the view's current selection, if any.
  if (props.initialCustomer) selectedCustomer.value = props.initialCustomer
  if (props.initialSite) selectedSite.value = props.initialSite
}

const submitForm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    isSubmitting.value = true

    const figures = {
      average_hourly_consumption_kwh: form.average_hourly_consumption_kwh!,
      cp_hour_load_kwh: form.cp_hour_load_kwh!,
      cp_schedule_rate_usd_per_kwh: form.cp_schedule_rate_usd_per_kwh!,
      total_savings_usd: form.total_savings_usd!,
      cp_management_fee_usd: form.cp_management_fee_usd!,
    }

    if (isEditMode.value && props.record) {
      const payload: ICustomerSavingsUpdatePayload = figures
      await updateCustomerSavingsMutation.mutateAsync({ id: props.record.id, payload })
    } else {
      if (!selectedSite.value?.site_id) return
      const payload: ICustomerSavingsCreationPayload = {
        site_id: selectedSite.value.site_id,
        year: form.year,
        month: form.month,
        ...figures,
      }
      await createCustomerSavingsMutation.mutateAsync(payload)
    }

    visible.value = false
  } catch {
    // Validation errors surface inline; API errors are notified by the mutation.
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <el-dialog
    v-model="visible"
    :title="isEditMode ? 'Edit Savings Record' : 'Add Savings Record'"
    width="550px"
    align-center
    @open="onDialogOpened"
    @closed="resetForm"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <!-- Site / period: editable on create, read-only on edit -->
      <template v-if="isEditMode">
        <div class="bg-black/5 rounded py-2 px-3 mb-[var(--spacing-sm)] flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <el-text type="info">Site</el-text>
            <span class="m-0 text-[var(--text-secondary)]">{{ record?.site.name }}</span>
          </div>
          <div class="flex items-center gap-2">
            <el-text type="info">Period</el-text>
            <span class="m-0 text-[var(--text-secondary)]">{{ editPeriodLabel }}</span>
          </div>
        </div>
      </template>

      <template v-else>
        <el-form-item label="Customer">
          <RcCustomerSelect v-model="selectedCustomer" placeholder="Select Customer" />
        </el-form-item>

        <el-form-item label="Site">
          <RcSiteSelect
            v-model="selectedSite"
            :disabled="!selectedCustomer"
            :customer-id="selectedCustomer?.id"
            placeholder="Select Site"
          />
        </el-form-item>

        <div class="flex gap-3">
          <el-form-item label="Month" class="flex-1">
            <el-select v-model="form.month" placeholder="Month">
              <el-option v-for="m in months" :key="m.value" :label="m.label" :value="m.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="Year" class="flex-1">
            <el-select v-model="form.year" placeholder="Year">
              <el-option v-for="y in years" :key="y" :label="y.toString()" :value="y" />
            </el-select>
          </el-form-item>
        </div>
      </template>

      <el-divider />

      <el-form-item label="Average hourly load, kWh" prop="average_hourly_consumption_kwh">
        <el-input-number
          v-model="form.average_hourly_consumption_kwh"
          class="w-full"
          :controls="false"
          :min="0"
          :precision="2"
          placeholder="0.00"
        />
      </el-form-item>

      <el-form-item label="CP hour load, KWh" prop="cp_hour_load_kwh">
        <el-input-number
          v-model="form.cp_hour_load_kwh"
          class="w-full"
          :controls="false"
          :min="0"
          :precision="2"
          placeholder="0.00"
        />
      </el-form-item>

      <el-form-item label="CP schedules rate, $/KWh" prop="cp_schedule_rate_usd_per_kwh">
        <el-input-number
          v-model="form.cp_schedule_rate_usd_per_kwh"
          class="w-full"
          :controls="false"
          :min="0"
          :precision="2"
          placeholder="0.00"
        />
      </el-form-item>

      <el-form-item label="Total savings, $" prop="total_savings_usd">
        <el-input-number
          v-model="form.total_savings_usd"
          class="w-full"
          :controls="false"
          :min="0"
          :precision="2"
          placeholder="0.00"
        />
      </el-form-item>

      <el-form-item label="CP management fee, $" prop="cp_management_fee_usd">
        <el-input-number
          v-model="form.cp_management_fee_usd"
          class="w-full"
          :controls="false"
          :min="0"
          :precision="2"
          placeholder="0.00"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <!-- The primary action is first in the DOM so Tab reaches it before Cancel after the
           last field; `order` keeps the usual visual layout (Cancel left, primary right). -->
      <div class="flex justify-end gap-3">
        <el-button
          size="large"
          type="primary"
          class="order-2"
          :disabled="isSubmitting || !canSubmit"
          :loading="isSubmitting"
          @click="submitForm"
        >
          {{ isEditMode ? 'Save' : 'Add' }}
        </el-button>
        <el-button size="large" class="order-1" @click="visible = false">Cancel</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
:deep(.el-input-number .el-input__inner) {
  text-align: left;
}
</style>
